import { STORAGE_PREFIX } from '../../config/env';
import { useSesionStore } from '../stores/sesionStore';

/**
 * Los filtros se guardan por persona.
 *
 * En un puesto compartido, heredar los filtros de quien usó la consola antes
 * haría creer que faltan datos. Sin sesión se usa una clave anónima, que se
 * queda con lo elegido en la pantalla de acceso y no estorba a nadie.
 */
function clave(modulo: string): string {
  const usuario = useSesionStore.getState().perfil?.id ?? 'anonimo';
  return `${STORAGE_PREFIX}filtros:${usuario}:${modulo}`;
}

/**
 * Recupera los filtros que este usuario dejó puestos en esa pantalla.
 *
 * Devuelve `null` ante cualquier problema —almacenamiento deshabilitado,
 * contenido corrupto de una versión anterior— en vez de propagar el fallo:
 * perder unos filtros es molesto, no poder abrir la pantalla es peor.
 */
export function leerFiltros<T>(modulo: string): Partial<T> | null {
  try {
    const guardado = localStorage.getItem(clave(modulo));
    if (!guardado) return null;

    const valor: unknown = JSON.parse(guardado);
    if (typeof valor !== 'object' || valor === null) return null;

    return valor as Partial<T>;
  } catch {
    return null;
  }
}

export function guardarFiltros<T extends object>(modulo: string, filtros: T): void {
  try {
    localStorage.setItem(clave(modulo), JSON.stringify(filtros));
  } catch {
    // Sin espacio o en modo privado: la pantalla funciona igual, sólo que
    // no recordará la selección la próxima vez.
  }
}

export function olvidarFiltros(modulo: string): void {
  try {
    localStorage.removeItem(clave(modulo));
  } catch {
    // Nada que hacer; el próximo guardado lo sobrescribirá.
  }
}
